import { useEffect, useRef, useState } from 'react'
import { webSocket, WebSocketSubject } from 'rxjs/webSocket'

import { useTelemetryCredentials } from '@/feature/telemetry/api/useMyTelemetryCredentials'

const RECONNECT_DELAY_MS = 5_000

export const useTripUpdates = (taskIds: string[] | undefined) => {
  const { data: credentials } = useTelemetryCredentials()
  const fleetName = credentials?.telemetryFleetName
  const telemetryToken = credentials?.telemetryToken
  const socketRef = useRef<WebSocketSubject<string> | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [tripProgress, setTripProgress] = useState<Record<string, number>>({})
  const [reconnectSignal, setReconnectSignal] = useState(0)
  const joinedIds = taskIds?.join(',')

  useEffect(() => {
    if (!taskIds?.length) return
    setTripProgress((prev) => {
      const idSet = new Set(taskIds)
      return Object.fromEntries(Object.entries(prev).filter(([id]) => idSet.has(id)))
    })
  }, [joinedIds])

  useEffect(() => {
    if (!fleetName || !telemetryToken || !joinedIds) return

    const wssHost = new URL(process.env.NEXT_PUBLIC_BRIDGE_TELEMETRY_WEBSOCKET_URL!).host
    const url = `wss://${wssHost}/v2/trip-updates/${fleetName}/${joinedIds}`

    const socket = webSocket<string>({
      url,
      deserializer: (e) => e.data,
      serializer: (e) => `${e}`,
    })

    socketRef.current = socket
    socket.next(telemetryToken)

    const subscription = socket.subscribe({
      next: (message) => {
        try {
          const parsed = JSON.parse(message)
          if (parsed['trip-id'] && parsed['percent-complete'] != null) {
            setTripProgress((prev) => ({
              ...prev,
              [parsed['trip-id']]: parsed['percent-complete'],
            }))
          }
        } catch {
          // status messages like :hello, :success — ignore
        }
      },
      error: (err) => {
        console.warn('[useTripUpdates] WebSocket disconnected, reconnecting in 5s', err)
        reconnectTimeoutRef.current = setTimeout(
          () => setReconnectSignal((n) => n + 1),
          RECONNECT_DELAY_MS,
        )
      },
      complete: () => console.log('[useTripUpdates] WebSocket closed'),
    })

    return () => {
      subscription.unsubscribe()
      socketRef.current = null
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }
  }, [fleetName, telemetryToken, joinedIds, reconnectSignal])

  return tripProgress
}
